import { useEffect } from 'react';
import { LiteSiteDetailContent } from '../../lite/components/LiteSiteDetailContent';
import { track } from '../../../services/telemetry';

type SiteDetailDrawerProps = {
  siteId: string;
  open: boolean;
  onClose: () => void;
};

// Pulldown sheet that fronts site details during the inspection flow.
// EP-LITEPARITY: mounts the same LiteSiteDetailContent as the browse page
// (/lite/sites/:id) — identical sections and inspector edit capabilities on
// both paths.
export function SiteDetailDrawer({
  siteId,
  open,
  onClose,
}: SiteDetailDrawerProps) {
  // Usage telemetry: once per drawer open (deliberately not keyed on the
  // unstable onClose identity, which would re-fire on parent re-renders).
  useEffect(() => {
    if (open) track('drawer_site_detail_opened', { site_id: siteId });
  }, [open, siteId]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-slate-900/50 dark:bg-slate-900/70 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute inset-x-0 bottom-0 max-h-[90vh] bg-slate-50 dark:bg-slate-900 rounded-t-2xl shadow-2xl border-t border-slate-200 dark:border-slate-700 animate-slide-up flex flex-col">
        <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
          <div className="w-10 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full" />
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4">
          <LiteSiteDetailContent siteId={siteId} onClose={onClose} />
        </div>
      </div>
    </div>
  );
}
